import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'

interface Action {
  id: string
  action_type: string
  label: string
  description: string | null
  real_at: string
  is_undone: boolean
  undo_policy: string
  payload: Record<string, unknown>
}

interface Effect {
  id: string
  target_table: string
  target_field: string
  operation: string
  old_value: unknown
  new_value: unknown
}

export function ActionHistoryPage() {
  const { id: characterId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { data: actions, isLoading } = useQuery({
    queryKey: ['action-history', characterId],
    enabled: !!characterId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('actions')
        .select('*')
        .eq('character_id', characterId!)
        .order('real_at', { ascending: false })

      if (error) throw error
      return data as Action[]
    },
  })

  const { data: effects } = useQuery({
    queryKey: ['action-effects', expandedId],
    enabled: !!expandedId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('effects')
        .select('*')
        .eq('action_id', expandedId!)

      if (error) throw error
      return data as Effect[]
    },
  })

  if (isLoading) return <div style={{ padding: '2rem' }}>Loading history...</div>

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>Action History</h2>
        <button onClick={() => navigate(`/character/${characterId}`)}>&larr; Character Sheet</button>
      </div>

      {(!actions || actions.length === 0) ? (
        <p style={{ color: '#666' }}>No actions recorded yet.</p>
      ) : (
        <div>
          {actions.map(action => (
            <div
              key={action.id}
              style={{
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '0.75rem',
                marginBottom: '0.5rem',
                opacity: action.is_undone ? 0.5 : 1,
                cursor: 'pointer',
              }}
              onClick={() => setExpandedId(expandedId === action.id ? null : action.id)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>{action.label}</strong>
                  {action.is_undone && (
                    <span style={{ marginLeft: '0.5rem', color: '#999', fontSize: '0.8rem' }}>(undone)</span>
                  )}
                  <span style={{ marginLeft: '0.75rem', color: '#666', fontSize: '0.85rem' }}>
                    {action.action_type}
                  </span>
                </div>
                <span style={{ fontSize: '0.8rem', color: '#999' }}>
                  {new Date(action.real_at).toLocaleString()}
                </span>
              </div>
              {action.description && (
                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                  {action.description}
                </div>
              )}

              {expandedId === action.id && effects && effects.length > 0 && (
                <div style={{ marginTop: '0.5rem', borderTop: '1px solid #eee', paddingTop: '0.5rem' }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>Effects:</div>
                  <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #ddd', textAlign: 'left' }}>
                        <th style={{ padding: '0.2rem' }}>Table</th>
                        <th>Field</th>
                        <th>Op</th>
                        <th>Old</th>
                        <th>New</th>
                      </tr>
                    </thead>
                    <tbody>
                      {effects.map(eff => (
                        <tr key={eff.id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '0.2rem' }}>{eff.target_table}</td>
                          <td>{eff.target_field}</td>
                          <td>{eff.operation}</td>
                          <td style={{ color: '#999' }}>{eff.old_value != null ? JSON.stringify(eff.old_value) : '—'}</td>
                          <td style={{ color: '#333' }}>{eff.new_value != null ? JSON.stringify(eff.new_value) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
